import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tags, ScanBarcode, Boxes, Truck, LogOut, ArrowUpRight, ArrowRight, Loader2, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const APPS = [
  { id: "label-manager", name: "Label Manager", desc: "Sort, print and track Amazon, Flipkart & Firstcry shipping labels by warehouse → SKU.", icon: Tags, to: "/dashboard", accent: "#2563EB" },
  { id: "parcel-scan", name: "Parcel Scan", desc: "Scan inbound and outbound parcels and reconcile them against orders.", icon: ScanBarcode, href: "https://parcel.gosnd.com", sso: "parcel", accent: "#F59E0B" },
  { id: "inventory", name: "Inventory Management", desc: "Stock levels, picklists, movements and warehouse transfers.", icon: Boxes, href: "https://inv.gosnd.com", sso: "inventory", accent: "#1C8552" },
  { id: "vms", name: "VMS", desc: "Vendor management system — purchase orders, vendors and receipts.", icon: Truck, href: "https://vms.gosnd.com", sso: "vms", accent: "#DB2777" },
];

const Tile = ({ app, i }) => {
  const [busy, setBusy] = useState(false);
  const openSso = async (e) => {
    e.preventDefault();
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const { data } = await api.post("/sso/code", { app: app.sso });
      if (win) win.location.href = data.url; else window.open(data.url, "_blank");
    } catch (err) {
      if (win) win.location.href = app.href;
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className="flex h-14 w-14 items-center justify-center rounded-md text-white shadow-lg" style={{ backgroundColor: app.accent }}>{busy ? <Loader2 className="h-7 w-7 animate-spin" /> : <app.icon className="h-7 w-7" />}</div>
        {app.href ? <ArrowUpRight className="h-5 w-5 text-slate-500 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-slate-200" /> : <ArrowRight className="h-5 w-5 text-slate-500 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-slate-200" />}
      </div>
      <div className="mt-6 font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50">{app.name}</div>
      <p className="mt-2 text-sm leading-relaxed text-slate-400">{app.desc}</p>
      <div className="mt-5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">{app.href ? app.href.replace("https://", "") : "This application"}{app.sso && <span className="flex items-center gap-1 rounded-sm border border-emerald-900 bg-emerald-950/40 px-1.5 py-0.5 text-emerald-300"><KeyRound className="h-3 w-3" /> Single sign-on</span>}</div>
    </>
  );
  const cls = "rise group flex flex-col rounded-lg border border-slate-800 bg-[#111827] p-6 text-left transition-all duration-200 hover:-translate-y-1 hover:border-slate-600 hover:shadow-2xl";
  const style = { animationDelay: `${i * 80}ms` };
  return app.href
    ? <a href={app.href} onClick={openSso} target="_blank" rel="noopener noreferrer" className={cls} style={style} data-testid={`hub-tile-${app.id}`}>{inner}</a>
    : <Link to={app.to} className={cls} style={style} data-testid={`hub-tile-${app.id}`}>{inner}</Link>;
};

export default function HubPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" data-testid="hub-page">
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <img src="/starandaisy-logo-white.png" alt="StarAndDaisy" className="h-12 w-auto" />
            <div className="leading-none">
              <div className="font-heading text-xl font-extrabold uppercase tracking-tight">Operations</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">StarAndDaisy · internal tools</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="hub-user" className="hidden font-mono text-xs text-slate-400 sm:inline">{user?.name || user?.email}{user?.emp_id && <span className="ml-1 text-slate-500">· {user.emp_id}</span>} <span className="ml-1 rounded-sm border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-slate-300">{user?.role}</span></span>
            <Button data-testid="hub-logout-button" variant="outline" size="sm" className="border-slate-700 bg-transparent hover:bg-slate-800" onClick={async () => { await logout(); navigate("/login"); }}><LogOut className="mr-1 h-4 w-4" /> Logout</Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8 sm:py-16">
        <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
        <h1 className="font-heading mt-2 text-4xl font-extrabold uppercase tracking-tight sm:text-5xl lg:text-6xl">Pick a workspace</h1>
        <p className="mt-3 max-w-xl text-base text-slate-400">Everything the ops team needs, one sign-in. External tools open in a new tab.</p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2" data-testid="hub-grid">
          {APPS.map((a, i) => <Tile key={a.id} app={a} i={i} />)}
        </div>
      </main>
    </div>
  );
}
